import { useState } from "react";
import { Link } from "@tanstack/react-router";

/**
 * "Is SEO right for your firm?" self-check.
 * Placement: blog post, section "Do Law Firms Need SEO? Is It Worth It?",
 * directly after the paragraph that starts "The third condition is the one firms skip."
 * Uses shadcn/Tailwind theme tokens so it inherits the site's colours.
 * Replace the <a> tags with the site's router Link component if preferred.
 */

type Answer = "yes" | "no" | null;

const QUESTIONS = [
  { id: "practice", text: "Is there a practice area you want more cases in?" },
  { id: "office", text: "Do you serve a defined area, with a real office there?" },
  { id: "intake", text: "Does someone answer new calls and web forms quickly, including after hours?" },
  { id: "patience", text: "Can you give SEO at least 6 to 12 months to build?" },
  { id: "urgent", text: "Do you need new cases coming in within the next 30 days?" },
] as const;

type QuestionId = (typeof QUESTIONS)[number]["id"];

type Result = {
  heading: string;
  body: string;
  links: { label: string; href: string }[];
};

function getResult(a: Record<QuestionId, Answer>): Result {
  if (a.intake === "no") {
    return {
      heading: "Fix intake first",
      body:
        "Ranking higher will send more people to a phone nobody answers. Set up fast call handling and after-hours coverage before you invest in SEO.",
      links: [{ label: "Lead generation for law firms", href: "/services/lead-generation-for-law-firms" }],
    };
  }
  if (a.practice === "no") {
    return {
      heading: "Choose your growth area first",
      body:
        "SEO needs a target. Decide which practice area you want more cases in, then build pages and content around it.",
      links: [{ label: "See SEO by practice area", href: "/practice-areas" }],
    };
  }
  if (a.urgent === "yes" || a.patience === "no") {
    return {
      heading: "Lead with ads, build SEO alongside",
      body:
        "SEO takes months. Paid search or Local Services Ads can bring calls in now while your Google Business Profile and core pages are built.",
      links: [
        { label: "PPC for law firms", href: "/services/ppc-for-law-firms" },
        { label: "Get a free SEO audit", href: "/law-firm-seo-audit" },
      ],
    };
  }
  if (a.office === "no") {
    return {
      heading: "SEO can work, with a narrower focus",
      body:
        "Without a real office in a market, the map pack is unlikely. Focus on organic results with strong practice area and state-level pages.",
      links: [{ label: "Get a free SEO audit", href: "/law-firm-seo-audit" }],
    };
  }
  return {
    heading: "Your firm is a good fit for SEO",
    body:
      "You have a target, a real local presence, working intake and the time SEO needs. The next step is an audit to see what is holding your site back.",
    links: [
      { label: "Get a free SEO audit", href: "/law-firm-seo-audit" },
      { label: "Law firm SEO services", href: "/services/law-firm-seo" },
    ],
  };
}

const emptyAnswers = Object.fromEntries(QUESTIONS.map((q) => [q.id, null])) as Record<QuestionId, Answer>;

export default function SeoFitCheck() {
  const [answers, setAnswers] = useState<Record<QuestionId, Answer>>(emptyAnswers);
  const answered = QUESTIONS.filter((q) => answers[q.id] !== null).length;
  const complete = answered === QUESTIONS.length;
  const result = complete ? getResult(answers) : null;

  const choose = (id: QuestionId, value: Exclude<Answer, null>) =>
    setAnswers((prev) => ({ ...prev, [id]: value }));

  return (
    <section
      aria-labelledby="seo-fit-check-title"
      className="not-prose my-10 rounded-xl border border-border bg-card p-6 text-card-foreground sm:p-8"
    >
      <h3 id="seo-fit-check-title" className="text-xl font-semibold">
        Is SEO right for your firm?
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Answer five questions. Your result appears when all five are answered.
      </p>

      <ol className="mt-6 space-y-4">
        {QUESTIONS.map((q, i) => (
          <li
            key={q.id}
            className="flex flex-col gap-3 border-b border-border pb-4 last:border-0 sm:flex-row sm:items-center sm:justify-between"
          >
            <span className="text-base">
              <span className="mr-2 text-muted-foreground">{i + 1}.</span>
              {q.text}
            </span>
            <div role="group" aria-label={q.text} className="flex shrink-0 gap-2">
              {(["yes", "no"] as const).map((v) => {
                const selected = answers[q.id] === v;
                return (
                  <button
                    key={v}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => choose(q.id, v)}
                    className={`min-w-16 rounded-md border px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                      selected
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-background hover:bg-muted"
                    }`}
                  >
                    {v === "yes" ? "Yes" : "No"}
                  </button>
                );
              })}
            </div>
          </li>
        ))}
      </ol>

      <div aria-live="polite" className="mt-6">
        {result ? (
          <div className="rounded-lg bg-muted p-5">
            <p className="text-lg font-semibold">{result.heading}</p>
            <p className="mt-2 text-base text-muted-foreground">{result.body}</p>
            <div className="mt-4 flex flex-wrap gap-3">
              {result.links.map((l) => (
                <Link
                  key={l.href}
                  to={l.href}
                  className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  {l.label}
                </Link>
              ))}
              <button
                type="button"
                onClick={() => setAnswers(emptyAnswers)}
                className="rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                Start over
              </button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            {answered} of {QUESTIONS.length} answered
          </p>
        )}
      </div>
    </section>
  );
}
